import { useEffect, useState, useCallback } from 'react';
import { api, ApiError } from '../api/client';
import './ResellerPanel.css';

interface ResellerStatus {
  totalPanels: number;
  pushedPanels: number;
  telegramPingedAt: string | null;
}

interface Props {
  orderId: string;
  /** Polling interval in ms while order isn't fully pushed. Default 5s. */
  pollIntervalMs?: number;
}

/** Order-detail dashboard component for the reseller forwarding pipeline.
 *  Shows the per-order push counter and a single bulk "Push to reseller"
 *  button that enqueues a BullMQ job. The component polls /reseller-status
 *  while the order isn't done so the counter reflects worker progress. */
export function ResellerPanel({ orderId, pollIntervalMs = 5_000 }: Props): JSX.Element {
  const [status, setStatus] = useState<ResellerStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pushing, setPushing] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const fetchStatus = useCallback(async (): Promise<ResellerStatus | null> => {
    try {
      const data = await api<ResellerStatus>(`/api/orders/${orderId}/reseller-status`);
      setStatus(data);
      setError(null);
      setLoading(false);
      return data;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load reseller status');
      setLoading(false);
      return null;
    }
  }, [orderId]);

  useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  // Poll while not fully done. "Done" = telegramPingedAt set AND every panel
  // is stamped. The auto-hook fires the job at order finalization, so the
  // operator typically sees the counter climb on its own.
  useEffect(() => {
    if (!status) return;
    const done =
      status.telegramPingedAt !== null && status.pushedPanels >= status.totalPanels;
    if (done) return;
    const handle = setInterval(() => void fetchStatus(), pollIntervalMs);
    return () => clearInterval(handle);
  }, [status, fetchStatus, pollIntervalMs]);

  const handleBulkPush = async (): Promise<void> => {
    setPushing(true);
    setFlash(null);
    try {
      await api<{ queued: boolean }>(`/api/orders/${orderId}/reseller-push`, {
        method: 'POST',
      });
      setFlash('Push queued. Watching for completion…');
      setTimeout(() => void fetchStatus(), 1000);
      setTimeout(() => setFlash(null), 8000);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'ALREADY_DONE') {
        setFlash('Already done — no new work to enqueue.');
        setTimeout(() => void fetchStatus(), 500);
      } else {
        setError(err instanceof ApiError ? err.message : 'Push failed');
      }
    } finally {
      setPushing(false);
    }
  };

  if (loading) return <div className="rs-panel rs-panel--loading">Loading reseller status…</div>;
  if (error) return <div className="rs-panel rs-panel--error">Reseller: {error}</div>;
  if (!status) return <div className="rs-panel">No reseller status</div>;

  const done =
    status.telegramPingedAt !== null && status.pushedPanels >= status.totalPanels;
  const remaining = status.totalPanels - status.pushedPanels;

  return (
    <div className={`rs-panel${done ? ' rs-panel--complete' : ''}`}>
      <div className="rs-panel__main">
        <div className="rs-panel__label">Reseller (Sheet + Telegram)</div>
        <div className="rs-panel__count">
          <span className="rs-panel__pushed">{status.pushedPanels}</span>
          <span className="rs-panel__sep"> / </span>
          <span className="rs-panel__total">{status.totalPanels}</span>
          <span className="rs-panel__suffix"> pushed</span>
          {status.telegramPingedAt && (
            <span className="rs-panel__telegram"> · ✓ Telegram pinged</span>
          )}
        </div>
        {flash !== null && <div className="rs-panel__flash">{flash}</div>}
      </div>
      {!done && (
        <button
          type="button"
          className="rs-panel__bulk-btn"
          onClick={() => void handleBulkPush()}
          disabled={pushing}
          title="Append rows to the reseller sheet and ping the Telegram group"
        >
          {pushing ? 'Queueing…' : remaining > 0 ? `Push to reseller (${remaining})` : 'Push to reseller'}
        </button>
      )}
    </div>
  );
}
